import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const [config, setConfig] = useState({ boost_enabled: false });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status !== 401) console.error("auth/me failed:", err);
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // CRITICAL: If returning from Google Auth callback, skip /me — AuthCallback will exchange
      // the session_id and establish our JWT first. (Emergent Google Auth playbook)
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        setReady(true);
        return;
      }
      await refresh();
      try {
        const { data } = await api.get("/config");
        setConfig(data);
      } catch (err) {
        console.error("config load failed:", err);
      }
      setReady(true);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("rn_token", data.token);
    // refresh() returns the user record from /auth/me — return it to callers
    // so pages (like LoginPage / AuthGateModal) can route by role right away.
    const fresh = await refresh();
    return fresh || data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("rn_token", data.token);
    await refresh();
    return data;
  };

  // Exchange an Emergent Google Auth session_id for our JWT + populate user context.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const exchangeGoogleSession = async (sessionId) => {
    const refCode = (typeof window !== "undefined" && window.localStorage.getItem("tlr_ref")) || null;
    const { data } = await api.post("/auth/google/session", {
      session_id: sessionId,
      referral_code: refCode,
    });
    if (data.token) localStorage.setItem("rn_token", data.token);
    if (refCode) localStorage.removeItem("tlr_ref");
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("logout failed:", err); }
    localStorage.removeItem("rn_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, config, login, register, logout, refresh, formatApiError, exchangeGoogleSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
